import axios from "@/redux/axios";

// Get authentication token from store (lazy loading to avoid circular dependency)
let getStore: (() => any) | null = null;

export const setStoreGetter = (storeGetter: () => any) => {
  getStore = storeGetter;
};

// Get WebSocket URL from environment or construct from API base URL
// New format: /ws/evaluation/{user_id}/{task_id}
const getWebSocketUrl = (userId: string, taskId: string): string => {
  // First, try to get WebSocket URL from environment variable
  const wsBaseUrl = import.meta.env.VITE_WEBSOCKET_URL;
  if (wsBaseUrl) {
    const wsPath = import.meta.env.VITE_WEBSOCKET_PATH || "/ws/evaluation";
    const url = `${wsBaseUrl}${wsPath}/${userId}/${taskId}`;
    // console.log("🔌 Using WebSocket URL from env:", url);
    return url;
  }

  // Otherwise, construct from API base URL
  const apiBaseUrl = axios.defaults.baseURL || "http://localhost:3000";
  // console.log("🔌 API Base URL:", apiBaseUrl);

  // Convert HTTP/HTTPS to WS/WSS
  let wsUrl = apiBaseUrl;

  // If it's localhost, use ws://, otherwise use wss://
  if (wsUrl.includes("localhost") || wsUrl.includes("127.0.0.1")) {
    wsUrl = wsUrl.replace(/^https?/, "ws");
  } else {
    // For production, always use wss://
    wsUrl = wsUrl.replace(/^https?/, "wss");
  }

  // New WebSocket path format: /ws/evaluation/{user_id}/{task_id}
  const wsPath = import.meta.env.VITE_WEBSOCKET_PATH || "/ws/evaluation";
  const finalUrl = `${wsUrl}${wsPath}/${userId}/${taskId}`;

  // console.log("🔌 Constructed WebSocket URL:", finalUrl);
  // console.log(
  //   "🔌 WebSocket Protocol:",
  //   wsUrl.startsWith("wss") ? "WSS (Secure)" : "WS (Non-secure)"
  // );
  // console.log("👤 User ID:", userId);
  console.log("📋 Task ID:", taskId);

  return finalUrl;
};

export interface WebSocketEvent {
  type: string;
  data: any;
  timestamp: number;
}

export interface WebSocketCallbacks {
  onOpen?: () => void;
  onMessage?: (event: WebSocketEvent) => void;
  onError?: (error: Event) => void;
  onClose?: (event: CloseEvent) => void;
}

class WebSocketManager {
  private ws: WebSocket | null = null;
  private taskId: string | null = null;
  private userId: string | null = null;
  private callbacks: WebSocketCallbacks = {};
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 3000; // 3 seconds
  private isManualClose = false;
  private events: WebSocketEvent[] = [];

  connect(taskId: string, callbacks: WebSocketCallbacks = {}): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.warn("⚠️ WebSocket already connected");
      return;
    }

    // If there's an existing connection, close it first
    if (this.ws) {
      // console.log("🔌 Closing existing WebSocket connection");
      this.ws.close();
      this.ws = null;
    }

    // Get user_id from Redux store
    let userId: string | null = null;
    if (getStore) {
      try {
        const store = getStore();
        const state = store.getState();
        userId = state.loginSignup?.user?.user_id || null;

        if (!userId) {
          console.error(
            "❌ User ID not found in Redux store. Cannot connect WebSocket."
          );
          console.error("❌ LoginSignup state:", state.loginSignup);
          callbacks.onError?.(
            new Error("User ID not found. Please login again.") as any
          );
          return;
        }
      } catch (error) {
        console.error("❌ Could not get user_id from store:", error);
        callbacks.onError?.(
          new Error("Failed to get user ID from store.") as any
        );
        return;
      }
    } else {
      console.error("❌ Store getter not initialized. Cannot get user_id.");
      callbacks.onError?.(new Error("Store not initialized.") as any);
      return;
    }

    this.taskId = taskId;
    this.userId = userId;
    this.callbacks = callbacks;
    this.isManualClose = false;
    this.events = [];

    // Construct WebSocket URL with new format: /ws/evaluation/{user_id}/{task_id}
    const wsUrl = getWebSocketUrl(userId, taskId);
    // console.log("🔌 WebSocketManager: Connecting to WebSocket");
    // console.log("🌐 WebSocket URL:", wsUrl);
    // console.log("👤 User ID:", userId);
    console.log("📋 Task ID:", taskId);

    try {
      this.ws = new WebSocket(wsUrl);
      // console.log("✅ WebSocket instance created");

      this.ws.onopen = () => {
        // console.log("✅ WebSocket connected successfully!");
        // console.log("📊 WebSocket readyState:", this.ws?.readyState);
        // console.log("📊 WebSocket protocol:", this.ws?.protocol);
        this.reconnectAttempts = 0;
        this.callbacks.onOpen?.();
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const wsEvent: WebSocketEvent = {
            type: data.type || "message",
            data: data.data || data,
            timestamp: Date.now(),
          };

          this.events.push(wsEvent);
          // console.log("📨 WebSocket message received:", wsEvent);
          this.callbacks.onMessage?.(wsEvent);
        } catch (error) {
          console.error("❌ Error parsing WebSocket message:", error);
          // Still call onMessage with raw data
          const wsEvent: WebSocketEvent = {
            type: "message",
            data: event.data,
            timestamp: Date.now(),
          };
          this.events.push(wsEvent);
          this.callbacks.onMessage?.(wsEvent);
        }
      };

      this.ws.onerror = (error) => {
        console.error("❌ WebSocket error occurred!");
        console.error("❌ Error event:", error);
        console.error("❌ WebSocket readyState:", this.ws?.readyState);
        console.error("❌ WebSocket URL:", wsUrl);
        // Try to get more error details
        if (this.ws) {
          console.error("❌ WebSocket protocol:", this.ws.protocol);
          console.error("❌ WebSocket extensions:", this.ws.extensions);
        }
        this.callbacks.onError?.(error);
      };

      this.ws.onclose = (event) => {
        // console.log("🔌 WebSocket closed");
        // console.log("📊 Close code:", event.code);
        // console.log("📊 Close reason:", event.reason || "No reason provided");
        // console.log("📊 Was clean:", event.wasClean);

        // Log close code meanings
        const closeCodeMeanings: { [key: number]: string } = {
          1000: "Normal Closure",
          1001: "Going Away",
          1002: "Protocol Error",
          1003: "Unsupported Data",
          1006: "Abnormal Closure (no close frame)",
          1007: "Invalid frame payload data",
          1008: "Policy Violation",
          1009: "Message too big",
          1010: "Mandatory Extension",
          1011: "Internal Server Error",
          1012: "Service Restart",
          1013: "Try Again Later",
          1014: "Bad Gateway",
          1015: "TLS Handshake",
        };
        if (closeCodeMeanings[event.code]) {
          // console.log("📊 Close code meaning:", closeCodeMeanings[event.code]);
        }

        this.callbacks.onClose?.(event);

        // Auto-reconnect only if:
        // 1. Not manually closed
        // 2. Not a normal closure (code 1000) - backend closes normally after completion
        // 3. Haven't exceeded max attempts
        if (
          !this.isManualClose &&
          event.code !== 1000 && // Don't reconnect on normal closure
          this.reconnectAttempts < this.maxReconnectAttempts
        ) {
          this.reconnectAttempts++;
          // console.log(
          //   `🔄 Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`
          // );
          setTimeout(() => {
            if (this.taskId && this.userId) {
              this.connect(this.taskId, this.callbacks);
            }
          }, this.reconnectDelay);
        } else if (event.code === 1000) {
          // console.log("✅ Normal closure (code 1000). No reconnection needed.");
        }
      };
    } catch (error) {
      console.error("❌ Failed to create WebSocket:", error);
      this.callbacks.onError?.(error as Event);
    }
  }

  disconnect(): void {
    this.isManualClose = true;
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.taskId = null;
    this.userId = null;
    this.events = [];
  }

  send(data: any): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      console.warn("⚠️ WebSocket is not connected");
    }
  }

  getEvents(): WebSocketEvent[] {
    return [...this.events];
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  getReadyState(): number {
    return this.ws?.readyState ?? WebSocket.CLOSED;
  }

  getTaskId(): string | null {
    return this.taskId;
  }

  getConnectionUrl(): string | null {
    if (!this.taskId || !this.userId) return null;
    return getWebSocketUrl(this.userId, this.taskId);
  }

  getUserId(): string | null {
    return this.userId;
  }
}

// Singleton instance
export const websocketManager = new WebSocketManager();

// Helper function to test WebSocket connection manually
export const testWebSocketConnection = (
  userId: string,
  taskId: string
): Promise<boolean> => {
  return new Promise((resolve) => {
    console.log("🧪 Testing WebSocket connection...");
    const testUrl = getWebSocketUrl(userId, taskId);
    console.log("🧪 Test URL:", testUrl);

    const testWs = new WebSocket(testUrl);

    const timeout = setTimeout(() => {
      testWs.close();
      console.error("🧪 Test failed: Connection timeout");
      resolve(false);
    }, 5000); // 5 second timeout

    testWs.onopen = () => {
      clearTimeout(timeout);
      console.log("🧪 Test successful: WebSocket connected!");
      testWs.close();
      resolve(true);
    };

    testWs.onerror = (error) => {
      clearTimeout(timeout);
      console.error("🧪 Test failed: WebSocket error", error);
      resolve(false);
    };

    testWs.onclose = (event) => {
      if (event.code !== 1000) {
        console.error("🧪 Test failed: WebSocket closed with code", event.code);
        resolve(false);
      }
    };
  });
};
